import { createContext, useContext, useState, useEffect, ReactNode } from "react";

interface AccessGateContextType {
  hasAccess: boolean;
  grantAccess: () => void;
  revokeAccess: () => void;
}

const AccessGateContext = createContext<AccessGateContextType | undefined>(undefined);

// Routes that bypass the coming soon page
const BYPASS_ROUTES = [
  "/split/",
  "/accept-invite",
  "/privacy-policy",
  "/terms-of-service",
  "/contact",
  "/refund-policy",
  "/shakatah-checkout",
  "/shakatah-split",
  "/oauth-success",
  "/customer-login",
  "/reset-password",
  "/merchant-login",
  "/merchant/onboarding",
  "/merchant-onboarding",
  "/merchant-dashboard",
  "/merchant/widget-guide",
  "/admin",
  "/login",
];

// Export this function first so it can be used in initial state
export function shouldBypassGate(pathname: string): boolean {
  return BYPASS_ROUTES.some(route => pathname.startsWith(route));
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function AccessGateProvider({ children }: { children: ReactNode }) {
  const [hasAccess, setHasAccess] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    
    // If already granted, return true
    if (localStorage.getItem("shakatah_access") === "granted") {
      return true;
    }
    
    // Check if on a bypass route - grant access immediately to prevent flash
    if (shouldBypassGate(window.location.pathname)) {
      localStorage.setItem("shakatah_access", "granted");
      return true;
    }
    
    // Check for OAuth success param
    const params = new URLSearchParams(window.location.search);
    if (params.get("oauth_success") === "true") {
      localStorage.setItem("shakatah_access", "granted");
      return true;
    }
    
    return false;
  });

  useEffect(() => {
    // Check URL params for access code
    const params = new URLSearchParams(window.location.search);
    const accessParam = params.get("access");
    
    if (accessParam?.toLowerCase() === "shakatah2025") {
      localStorage.setItem("shakatah_access", "granted");
      setHasAccess(true);
      // Clean up URL
      params.delete("access");
      const newUrl = params.toString() 
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const grantAccess = () => {
    localStorage.setItem("shakatah_access", "granted");
    setHasAccess(true);
  };

  const revokeAccess = () => {
    localStorage.removeItem("shakatah_access");
    setHasAccess(false);
  };

  return (
    <AccessGateContext.Provider value={{ hasAccess, grantAccess, revokeAccess }}>
      {children}
    </AccessGateContext.Provider>
  );
}

export function useAccessGate() {
  const context = useContext(AccessGateContext);
  if (!context) {
    throw new Error("useAccessGate must be used within AccessGateProvider");
  }
  return context;
}
